import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Application, Card } from 'entities/card/model/types';
import { EMI } from 'shared/model/types';
import { PURGE } from 'redux-persist';
import { RootState } from 'shared/config/store/store';
import { Account } from 'entities/account/model/types';

type CardState = {
  cards: Record<number, Card[]>;
  applications: Record<number, Application[]>;
  currentCard: number | null;
};
type CardReducer = {
  [EMI.Wallester]: CardState;
};

const initialState: CardReducer = {
  [EMI.Wallester]: {
    cards: {},
    applications: {},
    currentCard: null,
  },
};

const cardSlice = createSlice({
  name: 'cards',
  initialState,
  reducers: {
    setCards(
      state,
      action: PayloadAction<{ cards: Card[]; emi: EMI; accountId: number }>,
    ) {
      const { cards, emi, accountId } = action.payload;
      state[emi].cards[accountId] = cards;
      state[emi].currentCard = cards[0]?.id || null;
    },
    setCurrentCard(state, action: PayloadAction<{ emi: EMI; cardId: number }>) {
      const { emi, cardId } = action.payload;
      state[emi].currentCard = cardId;
    },
    updateCard(
      state,
      action: PayloadAction<{
        accountId: number;
        newCard: Partial<Card>;
        emi: EMI;
        cardId: number;
      }>,
    ) {
      const { accountId, newCard, emi, cardId } = action.payload;
      const cards = state[emi].cards[accountId];
      if (cards) {
        state[emi].cards[accountId] = cards.map((card) =>
          card.id === cardId ? { ...card, ...newCard } : card,
        );
      }
    },
    addCards(
      state,
      action: PayloadAction<{ accountId: number; cards: Card[]; emi: keyof CardReducer }>,
    ) {
      const { accountId, cards, emi } = action.payload;
      if (state[emi].cards[accountId]) {
        state[emi].cards[accountId] = [...state[emi].cards[accountId], ...cards];
      } else {
        state[emi].cards[accountId] = cards;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const { setCards, setCurrentCard, updateCard, addCards } =
  cardSlice.actions;
export default cardSlice.reducer;

const selectCardState = (state: RootState, emi: EMI) => state.cards[emi];

export const getAccountCards = (emi: EMI, accountId: number) =>
  createSelector(
    (state: RootState) => selectCardState(state, emi),
    (cardState) => cardState.cards[accountId] || [],
  );
export const getAccountApplications = (emi: EMI, accountId: number) =>
  createSelector(
    (state: RootState) => selectCardState(state, emi),
    (cardState) => cardState.applications[accountId] || [],
  );
export const getCards = (emi: EMI) =>
  createSelector(
    (state: RootState) => selectCardState(state, emi),
    (cardState) => cardState.cards || {},
  );
export const getApplications = (emi: EMI) =>
  createSelector(
    (state: RootState) => selectCardState(state, emi),
    (cardState) => cardState.applications || [],
  );
export const getCurrentCard = (emi: EMI) =>
  createSelector(
    (state: RootState) => selectCardState(state, emi),
    (cardState) => cardState.currentCard,
  );
